import { useCallback, useEffect, useState } from 'react';
import { businessDayOf, type ShiftRequest } from '@lemontaigu/staff';
import { useShiftRequestDecisions } from '@lemontaigu/staff/react';
import { fmtDayLabel, fmtHours, fmtTime, fromDatetimeLocal, toDatetimeLocal, type AdminEmployee } from './logic.js';

// « Demandes » (brief docs/STAFF-ACCESS-AND-SHIFT-REQUESTS-BRIEF.md §C): what
// employees send from staff.* — « corriger ce pointage » / « j'ai oublié de
// pointer ». One tap decides it: Accepter applies exactly what was asked,
// Modifier applies the times typed here, Refuser applies nothing. The decision
// and the time entry commit in ONE database transaction (staff_decide_shift_
// request), so this view never half-applies anything — it only reports.

const hoursBetween = (inIso: string, outIso: string): number =>
  (new Date(outIso).getTime() - new Date(inIso).getTime()) / 3_600_000;

const range = (inIso: string, outIso: string): string =>
  `${fmtTime(inIso)} → ${fmtTime(outIso)} (${fmtHours(hoursBetween(inIso, outIso))})`;

function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

const STATUS_LABEL: Record<ShiftRequest['status'], string> = {
  pending: 'En attente',
  accepted: 'Acceptée',
  modified: 'Modifiée',
  rejected: 'Refusée',
  cancelled: 'Retirée',
};

export function ShiftRequestsInbox({
  employees,
  onChanged,
}: {
  employees: AdminEmployee[];
  onChanged: () => void;
}) {
  const decisions = useShiftRequestDecisions();
  const [pending, setPending] = useState<ShiftRequest[] | null>(null);
  const [recent, setRecent] = useState<ShiftRequest[]>([]);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const [p, r] = await Promise.all([decisions.pending(), decisions.recent(30)]);
      setPending(p);
      setRecent(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [decisions]);

  useEffect(() => {
    void load();
  }, [load]);

  const nameOf = (id: string): string => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.firstName} ${e.lastName}` : 'Employé·e inconnu·e';
  };

  const changed = () => {
    onChanged();
    void load();
  };

  return (
    <section className="sr-inbox">
      {err && <p className="auth-err">Erreur : {err}</p>}
      {pending === null && !err && <p className="staff-empty">Chargement…</p>}
      {pending !== null && pending.length === 0 && (
        <p className="staff-empty">Aucune demande en attente.</p>
      )}
      {pending?.map((r) => (
        <RequestCard key={r.id} request={r} name={nameOf(r.employeeId)} onDecided={changed} />
      ))}

      {recent.length > 0 && (
        <>
          <h3 className="section-title sr-recent-title">Dernières décisions</h3>
          <ul className="sr-recent">
            {recent.map((r) => (
              <li key={r.id} className={`sr-status-${r.status}`}>
                <strong>{nameOf(r.employeeId)}</strong> — {fmtDayLabel(businessDayOf(new Date(r.reqClockIn)))}{' '}
                <span className="sr-chip">{STATUS_LABEL[r.status]}</span>
                {r.finalClockIn && r.finalClockOut && <> · {range(r.finalClockIn, r.finalClockOut)}</>}
                {r.decisionNote && <em> · « {r.decisionNote} »</em>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function RequestCard({
  request,
  name,
  onDecided,
}: {
  request: ShiftRequest;
  name: string;
  onDecided: () => void;
}) {
  const decisions = useShiftRequestDecisions();
  const [mode, setMode] = useState<'idle' | 'modify' | 'reject'>('idle');
  const [inValue, setInValue] = useState(toDatetimeLocal(request.reqClockIn));
  const [outValue, setOutValue] = useState(toDatetimeLocal(request.reqClockOut));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const run = async (act: () => Promise<void>) => {
    setBusy(true);
    setErr('');
    try {
      await act();
    } catch (e) {
      // The RPC's messages are written for this reader (« le pointage a changé
      // depuis la demande — modifiez ou refusez »), so they are shown as is.
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setBusy(false);
    onDecided();
  };

  const accept = () => run(() => decisions.decide(request.id, { kind: 'accept' }));
  const reject = () => run(() => decisions.decide(request.id, { kind: 'reject' }, note));
  const modify = () => {
    const clockInIso = fromDatetimeLocal(inValue);
    const clockOutIso = fromDatetimeLocal(outValue);
    if (!clockInIso || !clockOutIso) {
      setErr('Arrivée et départ sont requis');
      return Promise.resolve();
    }
    return run(() => decisions.decide(request.id, { kind: 'modify', clockInIso, clockOutIso }, note));
  };

  const isChange = request.kind === 'change' && request.origClockIn && request.origClockOut;

  return (
    <article className="sr-card">
      <header className="sr-head">
        <strong>{name}</strong>
        <span>{fmtDayLabel(businessDayOf(new Date(request.reqClockIn)))}</span>
        <span className="sr-age">{ago(request.createdAt)}</span>
      </header>

      <div className="sr-diff">
        {isChange ? (
          <>
            <span className="sr-before">{range(request.origClockIn!, request.origClockOut!)}</span>
            <span aria-hidden="true"> → </span>
            <strong className="sr-after">{range(request.reqClockIn, request.reqClockOut)}</strong>
          </>
        ) : (
          <>
            <span className="sr-chip">Ajout</span> <strong className="sr-after">{range(request.reqClockIn, request.reqClockOut)}</strong>
          </>
        )}
      </div>
      {request.reason && <p className="sr-reason">« {request.reason} »</p>}

      {mode === 'idle' && (
        <div className="cand-actions">
          <button type="button" className="auth-btn" disabled={busy} onClick={() => void accept()}>
            Accepter
          </button>
          <button type="button" disabled={busy} onClick={() => setMode('modify')}>
            Modifier
          </button>
          <button type="button" className="ts-danger" disabled={busy} onClick={() => setMode('reject')}>
            Refuser
          </button>
        </div>
      )}

      {mode === 'modify' && (
        <div className="ts-add">
          <label className="ts-field">
            <span>Arrivée</span>
            <input type="datetime-local" value={inValue} onChange={(e) => setInValue(e.target.value)} />
          </label>
          <label className="ts-field">
            <span>Départ</span>
            <input type="datetime-local" value={outValue} onChange={(e) => setOutValue(e.target.value)} />
          </label>
          <label className="ts-field">
            <span>Message à l’employé·e (optionnel)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="cand-actions">
            <button type="button" className="auth-btn" disabled={busy} onClick={() => void modify()}>
              Valider avec ces heures
            </button>
            <button type="button" className="cand-dismiss" disabled={busy} onClick={() => setMode('idle')}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {mode === 'reject' && (
        <div className="ts-add">
          <label className="ts-field">
            <span>Motif du refus (optionnel)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="cand-actions">
            <button type="button" className="ts-danger" disabled={busy} onClick={() => void reject()}>
              Confirmer le refus
            </button>
            <button type="button" className="cand-dismiss" disabled={busy} onClick={() => setMode('idle')}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {err && <p className="auth-err">{err}</p>}
    </article>
  );
}
